import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiDetail } from "@/lib/api";
import { isLocalApp } from "@/lib/env";
import { useAuth } from "@/context/AuthContext";
import ProposalThanksScreen from "@/components/ProposalThanksScreen";
import {
  BarterAgreementDocument,
  BarterAgreementSignBox,
  MOCK_BARTER_AGREEMENT_FIELDS,
  normalizeBarterFields,
} from "@/components/BarterAgreementDocument";

const MOCK_TOKEN = "mock";
const MOCK_STUDIO_TOKEN = "mock-studio";

function mockPublicPayload({ status = "pending", canStudioSign = false } = {}) {
  const fields = normalizeBarterFields(MOCK_BARTER_AGREEMENT_FIELDS);
  const partnerSigned = status === "partner_signed" || status === "signed";
  const studioSigned = status === "signed";
  return {
    client_name: fields.PARTNER,
    status,
    fields,
    can_sign: status === "pending",
    can_studio_sign: canStudioSign && status === "partner_signed",
    partner_signed_at: partnerSigned ? new Date().toISOString() : null,
    partner_signature: partnerSigned ? null : null,
    studio_signed_at: studioSigned ? new Date().toISOString() : null,
    studio_signature: null,
    signed_at: studioSigned ? new Date().toISOString() : partnerSigned ? new Date().toISOString() : null,
    signature_name: partnerSigned ? fields.FOUNDER : null,
    expires_at: null,
    expired: false,
  };
}

function StudioToggle({ screen, onChange }) {
  const items = [
    { id: "unsigned", label: "Client · unsigned" },
    { id: "partner", label: "Partner signed" },
    { id: "both", label: "Both signed" },
    { id: "thanks", label: "Thanks" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="barter-agreement-studio-toggle">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onChange(item.id)}
          className={`text-[11px] uppercase tracking-[0.14em] ${
            screen === item.id ? "text-[var(--t-accent)]" : "text-[var(--t-meta)]"
          }`}
          data-testid={`barter-mock-${item.id}`}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

function AgreementBody({
  data,
  busy,
  onPartnerSign,
  onStudioSign,
  showStudioPad = false,
  studioToggle = null,
}) {
  const fields = normalizeBarterFields(data?.fields || {});
  const status = data?.status;
  const canPartnerSign = Boolean(data?.can_sign) && status === "pending";
  // TAĪSTU pad only when the host explicitly enables studio mode (admin preview / local mock-studio).
  const canStudioSign = Boolean(showStudioPad) && Boolean(data?.can_studio_sign);

  return (
    <div className="min-h-screen bg-[var(--t-bg)]" data-testid="barter-agreement-page">
      {studioToggle ? (
        <div className="sticky top-0 z-20 flex flex-wrap items-center justify-end gap-3 border-b border-[var(--t-border)]/60 bg-[var(--t-bg)]/90 px-4 py-3 backdrop-blur-sm">
          {studioToggle}
        </div>
      ) : null}
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 md:py-14">
        <div className="label-eyebrow mb-2">TAĪSTU</div>
        <h1 className="font-heading text-3xl tracking-tight !font-extralight md:text-4xl">
          Agreement
        </h1>
        <p className="mt-2 text-sm text-[var(--t-meta)]">{data?.client_name || fields.PARTNER}</p>
        {data?.expired ? (
          <p className="mt-4 text-sm text-[var(--t-accent)]" data-testid="barter-agreement-expired">
            This agreement link has expired.
          </p>
        ) : null}

        <div className="mt-8">
          <BarterAgreementDocument
            fields={fields}
            partnerSignature={data?.partner_signature}
            partnerSignedAt={data?.partner_signed_at}
            studioSignature={data?.studio_signature}
            studioSignedAt={data?.studio_signed_at}
            clauses={data?.clauses}
          />
        </div>

        {canPartnerSign ? (
          <BarterAgreementSignBox
            label={`${fields.FOUNDER || "Partner"} · signature`}
            partyLabel="Partner"
            busy={busy}
            onSign={onPartnerSign}
          />
        ) : null}

        {canStudioSign && !data?.studio_signed_at ? (
          <BarterAgreementSignBox
            label="TAĪSTU · Taī"
            partyLabel="Studio"
            submitLabel="Sign & send copy"
            busy={busy}
            onSign={onStudioSign}
          />
        ) : null}

        {status === "partner_signed" && !showStudioPad ? (
          <p className="mt-8 text-sm text-[var(--t-meta)]" data-testid="barter-awaiting-studio">
            Signed — waiting on TAĪSTU to countersign. You’ll receive a copy by email once both
            signatures are on the agreement.
          </p>
        ) : null}

        <p className="mt-12 text-center text-[11px] tracking-[0.08em] text-[var(--t-meta)]">
          <Link to="/privacy" className="hover:text-[var(--t-accent)]">
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
}

/** Local mock — filled Lumen & Clay agreement + studio screen toggles. */
function MockBarterAgreementPage({ studio = false }) {
  const [screen, setScreen] = useState(studio ? "unsigned" : "unsigned");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(() => mockPublicPayload({ canStudioSign: studio }));
  const [localPartnerSig, setLocalPartnerSig] = useState(null);
  const [localStudioSig, setLocalStudioSig] = useState(null);

  useEffect(() => {
    document.title = "Lumen & Clay x TAĪSTU";
    return () => {
      document.title = "TAĪSTU | Client Portal";
    };
  }, []);

  const fields = MOCK_BARTER_AGREEMENT_FIELDS;

  const applyScreen = (next) => {
    setScreen(next);
    if (next === "unsigned") {
      setData(mockPublicPayload({ status: "pending", canStudioSign: studio }));
      setLocalPartnerSig(null);
      setLocalStudioSig(null);
    } else if (next === "partner") {
      setData({
        ...mockPublicPayload({ status: "partner_signed", canStudioSign: studio }),
        partner_signature: localPartnerSig,
      });
      setLocalStudioSig(null);
    } else if (next === "both" || next === "thanks") {
      setData({
        ...mockPublicPayload({ status: "signed", canStudioSign: studio }),
        partner_signature: localPartnerSig,
        studio_signature: localStudioSig,
      });
    }
  };

  if (studio && screen === "thanks") {
    return (
      <>
        <div className="fixed left-0 right-0 top-0 z-20 flex flex-wrap items-center justify-end gap-3 px-4 py-3">
          <StudioToggle screen={screen} onChange={applyScreen} />
        </div>
        <ProposalThanksScreen
          clientName={fields.PARTNER}
          optionLabel="Agreement"
          personName={fields.FOUNDER}
          variant="barter"
        />
      </>
    );
  }

  if (!studio && data.status === "signed") {
    return (
      <ProposalThanksScreen
        clientName={fields.PARTNER}
        optionLabel="Agreement"
        personName={fields.FOUNDER}
        variant="barter"
      />
    );
  }

  const viewData = {
    ...data,
    partner_signature: data.partner_signature || localPartnerSig,
    studio_signature: data.studio_signature || localStudioSig,
  };

  return (
    <AgreementBody
      data={viewData}
      busy={busy}
      showStudioPad={studio}
      studioToggle={studio ? <StudioToggle screen={screen} onChange={applyScreen} /> : null}
      onPartnerSign={(stroke) => {
        setBusy(true);
        const stamp = new Date().toISOString();
        setLocalPartnerSig(stroke);
        setData({
          ...mockPublicPayload({ status: "partner_signed", canStudioSign: studio }),
          partner_signature: stroke,
          partner_signed_at: stamp,
        });
        setBusy(false);
        if (studio) setScreen("partner");
        toast.success("Partner signature captured.");
      }}
      onStudioSign={
        studio
          ? (stroke) => {
              setBusy(true);
              const stamp = new Date().toISOString();
              setLocalStudioSig(stroke);
              setData({
                ...mockPublicPayload({ status: "signed", canStudioSign: studio }),
                partner_signature: localPartnerSig || viewData.partner_signature,
                partner_signed_at: viewData.partner_signed_at || stamp,
                studio_signature: stroke,
                studio_signed_at: stamp,
              });
              setBusy(false);
              setScreen("thanks");
              toast.success("Signed — copy would email the partner.");
            }
          : undefined
      }
    />
  );
}

/** Live public agreement (partner signature) — token or partner slug. */
function LiveBarterAgreementPage({ accessKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const key = String(accessKey || "").trim();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: row } = await api.get(`/public/barter-agreement/${encodeURIComponent(key)}`);
        if (cancelled) return;
        setData(row);
        document.title = `${row?.client_name || "Partner"} x TAĪSTU`;
      } catch (e) {
        if (!cancelled) {
          setError(formatApiDetail(e.response?.data?.detail, "Agreement not found"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      document.title = "TAĪSTU | Client Portal";
    };
  }, [key]);

  const onPartnerSign = async (stroke) => {
    if (!stroke || busy) return;
    setBusy(true);
    try {
      const { data: row } = await api.post(`/public/barter-agreement/${encodeURIComponent(key)}/sign`, {
        signature: stroke,
      });
      setData(row);
      toast.success("Signature captured.");
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "Could not sign agreement"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="p-12 text-[var(--t-meta)]">Loading…</div>;
  }
  if (error) {
    return (
      <div className="mx-auto max-w-lg px-6 py-20 text-center" data-testid="barter-agreement-missing">
        <p className="font-heading text-2xl !font-extralight tracking-tight">{error}</p>
        <p className="mt-3 text-sm text-[var(--t-meta)]">Ask your TAĪSTU contact for a fresh link.</p>
      </div>
    );
  }

  // Fully executed — partner sees thanks after both parties signed (email already sent).
  if (data?.status === "signed") {
    return (
      <ProposalThanksScreen
        clientName={data.client_name || data.fields?.PARTNER || "Partner"}
        optionLabel="Agreement"
        personName={data.signature_name || data.fields?.FOUNDER}
        variant="barter"
      />
    );
  }

  return (
    <AgreementBody
      data={data}
      busy={busy}
      showStudioPad={false}
      onPartnerSign={onPartnerSign}
      onStudioSign={undefined}
    />
  );
}

/** Admin studio preview — open from the PDF link on the prospect record. */
function StudioBarterAgreementPreview({ partnerKey }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [resolvedId, setResolvedId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const clientKey = String(partnerKey || "").trim();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        if (!isAdmin) {
          if (!cancelled) setError("Admin only");
          return;
        }
        if (isLocalApp() && (clientKey === "mock-barter-lumen-clay" || clientKey === "mock-barter-inquiry-fern")) {
          const { getMockBarterClient } = await import("@/lib/mockBarterClients");
          const mock = getMockBarterClient(clientKey);
          const fields = normalizeBarterFields(mock?.barter_agreement?.fields || MOCK_BARTER_AGREEMENT_FIELDS);
          if (!cancelled) {
            setResolvedId(clientKey);
            setData({
              client_name: fields.PARTNER,
              status: mock?.barter_agreement?.status || "draft",
              fields,
              can_sign: false,
              can_studio_sign: mock?.barter_agreement?.status === "partner_signed",
              partner_signed_at: mock?.barter_agreement?.partner_signed_at || null,
              partner_signature: mock?.barter_agreement?.partner_signature || null,
              studio_signed_at: mock?.barter_agreement?.studio_signed_at || null,
              studio_signature: mock?.barter_agreement?.studio_signature || null,
              signature_name: mock?.barter_agreement?.signature_name || fields.FOUNDER,
            });
            document.title = `${fields.PARTNER} x TAĪSTU`;
          }
          return;
        }
        const { data: row } = await api.get(`/admin/clients/${encodeURIComponent(clientKey)}/barter-agreement`);
        const agreement = row?.barter_agreement || {};
        const fields = normalizeBarterFields(agreement.fields || {});
        if (cancelled) return;
        setResolvedId(row?.client_id || clientKey);
        setData({
          client_name: fields.PARTNER || "Partner",
          status: agreement.status || "draft",
          fields,
          can_sign: false,
          can_studio_sign: agreement.status === "partner_signed",
          partner_signed_at: agreement.partner_signed_at || null,
          partner_signature: agreement.partner_signature || null,
          studio_signed_at: agreement.studio_signed_at || null,
          studio_signature: agreement.studio_signature || null,
          signature_name: agreement.signature_name || fields.FOUNDER,
        });
        document.title = `${fields.PARTNER || "Partner"} x TAĪSTU`;
      } catch (e) {
        if (!cancelled) {
          setError(formatApiDetail(e.response?.data?.detail, "Agreement not found"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      document.title = "TAĪSTU | Client Portal";
    };
  }, [clientKey, isAdmin]);

  const onStudioSign = async (stroke) => {
    if (!stroke || busy || !isAdmin) return;
    const id = resolvedId || clientKey;
    setBusy(true);
    try {
      const { data: row } = await api.post(`/admin/clients/${encodeURIComponent(id)}/barter-agreement/sign`, {
        signature: stroke,
      });
      const agreement = row?.barter_agreement || row || {};
      const fields = normalizeBarterFields(agreement.fields || data?.fields || {});
      setData({
        client_name: fields.PARTNER || data?.client_name,
        status: agreement.status || "signed",
        fields,
        can_sign: false,
        can_studio_sign: false,
        partner_signed_at: agreement.partner_signed_at || data?.partner_signed_at,
        partner_signature: agreement.partner_signature || data?.partner_signature,
        studio_signed_at: agreement.studio_signed_at || new Date().toISOString(),
        studio_signature: agreement.studio_signature || stroke,
        signature_name: agreement.signature_name || fields.FOUNDER,
      });
      toast.success("Signed — copy emailed to the partner.");
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "Could not countersign"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="p-12 text-[var(--t-meta)]">Loading…</div>;
  }
  if (error) {
    return (
      <div className="mx-auto max-w-lg px-6 py-20 text-center">
        <p className="font-heading text-2xl !font-extralight tracking-tight">{error}</p>
      </div>
    );
  }

  return (
    <AgreementBody
      data={data}
      busy={busy}
      showStudioPad={isAdmin}
      onPartnerSign={undefined}
      onStudioSign={isAdmin ? onStudioSign : undefined}
    />
  );
}

export default function BarterAgreementPage() {
  const { token, partnerSlug, clientId } = useParams();
  const { user, loading: authLoading } = useAuth() || {};
  const tok = String(token || "").trim();
  const previewKey = String(partnerSlug || clientId || "").trim();
  const isAdmin = user?.role === "admin";

  if (previewKey) {
    if (authLoading) {
      return <div className="p-12 text-[var(--t-meta)]">Loading…</div>;
    }
    if (isAdmin) {
      return <StudioBarterAgreementPreview partnerKey={previewKey} />;
    }
    return <LiveBarterAgreementPage accessKey={previewKey} />;
  }

  if (isLocalApp() && (tok === MOCK_TOKEN || tok === MOCK_STUDIO_TOKEN)) {
    return <MockBarterAgreementPage studio={tok === MOCK_STUDIO_TOKEN} />;
  }

  return <LiveBarterAgreementPage accessKey={tok} />;
}

export { MOCK_TOKEN as BARTER_AGREEMENT_MOCK_TOKEN, MOCK_STUDIO_TOKEN as BARTER_AGREEMENT_MOCK_STUDIO_TOKEN };
